/**
 * DI-2-9 — small fetch helper for the `intake/v1` namespace (DI-D34 c2: the
 * browser intake's own domain — log candidates, match, status, points,
 * flights).
 *
 * Mirrors `inspection3Fetch.ts` (itself the `timeTrackingFetch.ts` pattern):
 * the surface is TENANT-SCOPED, so every call sends `X-Tenant-ID` alongside
 * the bearer token. The base URL comes from `getIntakeBaseUrl()` (wired by
 * DI-2-4) so `DEV_API_MODE` routes requests correctly. Errors carry the parsed
 * JSON body so callers can read the envelope — the 409 `reason`
 * (`match_already_running`, `no_log_candidates`, `intake_state_invalid`,
 * `da_not_created`) and the 422 `reason` (`serial_not_on_turbine` with
 * `serial` + `onTurbine`).
 */
import { getIntakeBaseUrl, useAuthStore } from "@aeriosiq/auth-client";
import { tenantForCall } from "./setTenants.js";
import { INTAKE_REQUEST_DEADLINE_MS, withDeadline } from "./deadline.js";

export interface IntakeFetchError extends Error {
  status: number;
  body: unknown;
}

interface IntakeFetchOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /**
   * M3C-04 — the tenant this call is for: a string names it (a create under
   * a shared injection node, a list run for a shared owner); null forces the
   * caller's own; absent → the tenant of the set the path addresses
   * (`setTenants.ts`), else the caller's own.
   */
  tenantSlug?: string | null;
  /**
   * DI-3-S68 B1 — this request kind's deadline (default
   * `INTAKE_REQUEST_DEADLINE_MS`); past it the call rejects with
   * `RequestTimeoutError`, a transport failure (`deadline.ts`).
   */
  timeoutMs?: number;
}

/**
 * The ONE place (with its sibling helper) a set's tenant becomes the
 * `X-Tenant-ID` header (lock `one-header-place`, M3C-04 AC-4): the set's
 * `targetTenantSlug` when it has one, else the auth store's slug.
 */
function authHeaders(target: string | null): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  const slug = target ?? tenant?.tenantSlug ?? null;
  if (slug) {
    headers.set("X-Tenant-ID", slug);
  }
  return headers;
}

export async function intakeFetch<T>(
  path: string,
  opts: IntakeFetchOptions = {},
): Promise<T> {
  const headers = authHeaders(tenantForCall(path, opts.tenantSlug));
  const init: RequestInit = { method: opts.method ?? "GET", headers };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  return withDeadline(
    path,
    opts.timeoutMs ?? INTAKE_REQUEST_DEADLINE_MS,
    async (signal) => {
      init.signal = signal;
      const res = await fetch(`${getIntakeBaseUrl()}${path}`, init);
      return readAnswer<T>(path, res);
    },
  );
}

/** The answer: the parsed body, or the error carrying status + envelope. */
async function readAnswer<T>(path: string, res: Response): Promise<T> {
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — the status still surfaces.
    }
    const err = new Error(
      `intake fetch failed: ${path} → ${String(res.status)}`,
    ) as IntakeFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  return JSON.parse(text) as T;
}

export function isIntakeFetchError(e: unknown): e is IntakeFetchError {
  return (
    e instanceof Error &&
    typeof (e as Partial<IntakeFetchError>).status === "number"
  );
}

/** The envelope's `reason`, when the body carries one. */
export function reasonOf(e: unknown): string | null {
  if (!isIntakeFetchError(e)) return null;
  const b = e.body as { reason?: unknown } | null;
  return typeof b?.reason === "string" ? b.reason : null;
}
